import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { AppSidebarItem } from '@/components/layout/AppSidebarItem'
import { OrgSwitcher } from '@/components/layout/OrgSwitcher'
import { SidebarUserMenu } from '@/components/layout/SidebarUserMenu'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar'
import { FEATURE_FLAGS } from '@/constants/featureFlags'
import { useExperienceLabAccess } from '@/hooks/useExperienceLabAccess'
import { useOrganizationFeatureFlag } from '@/hooks/useOrganizationFeatureFlag'
import { buildNav, NAV_GROUPS } from '@/lib/navConfig'
import { useAuthStore } from '@/stores/auth'

export function AppSidebar() {
  const location = useLocation()
  const user = useAuthStore((state) => state.user)
  const organization = useAuthStore((state) => state.organization)
  const hasPermission = useAuthStore((state) => state.hasPermission)
  const { isMobile, setOpenMobile } = useSidebar()
  const { allowed: experienceLabAllowed } = useExperienceLabAccess()
  const { enabled: siteHealthEnabled } = useOrganizationFeatureFlag(
    FEATURE_FLAGS.SITE_HEALTH
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: Route changes intentionally close the persistent mobile sheet.
  useEffect(() => {
    if (isMobile) setOpenMobile(false)
  }, [isMobile, location.pathname, setOpenMobile])

  const nav = useMemo(
    () =>
      buildNav(NAV_GROUPS, {
        hasUser: user !== null,
        hasPermission,
        hasCdpConnection: organization?.hasCdpConnection ?? false,
        experienceLabAllowed,
        siteHealthEnabled,
      }),
    [
      experienceLabAllowed,
      hasPermission,
      organization?.hasCdpConnection,
      siteHealthEnabled,
      user,
    ]
  )

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <OrgSwitcher />
      </SidebarHeader>

      <SidebarContent>
        {nav.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => (
                  <AppSidebarItem key={item.label} item={item} />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarUserMenu />
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  )
}
